import React from 'react';
import { CheckCircle, Pending, Warning } from '@mui/icons-material';

export const BOOKING_PAYMENT_TOLERANCE = 0.01;

export function roundBookingMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

function resolveSegmentPaymentStatus(totalAmount, totalPaid, remainingAmount) {
  if (totalAmount <= BOOKING_PAYMENT_TOLERANCE) return 'no_payment';
  if (remainingAmount <= BOOKING_PAYMENT_TOLERANCE) return 'full_payment';
  if (totalPaid <= BOOKING_PAYMENT_TOLERANCE) return 'due';
  return 'partial_payment';
}

function resolveHotelPaymentSlice(booking) {
  const currency = booking?.currency || 'INR';
  const normalizedBackendStatus = String(booking?.payment_status || '').trim().toLowerCase();
  const totalAmount = roundBookingMoney(
    booking?.total_amount
    ?? booking?.amount
    ?? booking?.payment_summary?.total_amount
    ?? 0,
  );
  let totalPaid = roundBookingMoney(
    booking?.total_paid
    ?? booking?.payment_summary?.total_paid
    ?? 0,
  );
  let remainingAmount = roundBookingMoney(
    booking?.remaining_amount
    ?? booking?.payment_summary?.remaining_balance
    ?? Math.max(0, totalAmount - totalPaid),
  );

  if (remainingAmount <= BOOKING_PAYMENT_TOLERANCE) {
    remainingAmount = 0;
  }

  const backendMarkedPaid =
    booking?.is_paid === true
    || normalizedBackendStatus === 'full_payment'
    || normalizedBackendStatus === 'paid';

  if (remainingAmount <= 0 && totalAmount > 0) {
    if (totalPaid <= 0 && backendMarkedPaid) {
      totalPaid = totalAmount;
    }
    remainingAmount = 0;
  }

  const paymentStatus = resolveSegmentPaymentStatus(totalAmount, totalPaid, remainingAmount);
  const hasOutstandingBalance = remainingAmount > BOOKING_PAYMENT_TOLERANCE;

  return {
    currency,
    totalAmount,
    totalPaid,
    remainingAmount,
    paymentStatus,
    isDue: hasOutstandingBalance,
    showPaidBreakdown: totalPaid > 0 && hasOutstandingBalance,
    showDueBreakdown: hasOutstandingBalance,
  };
}

function resolveRestaurantPaymentSlice(booking, currency = 'INR') {
  const payload = booking?.restaurant_orders_summary;
  if (!payload?.has_orders) {
    return {
      hasRestaurant: false,
      totalAmount: 0,
      totalPaid: 0,
      remainingAmount: 0,
      paymentStatus: 'no_payment',
      isDue: false,
      showPaidBreakdown: false,
      showDueBreakdown: false,
      currency,
    };
  }
  const summary = payload.summary || {};
  const totalAmount = roundBookingMoney(summary.total_amount ?? 0);
  const totalPaid = roundBookingMoney(summary.paid_amount ?? 0);
  let remainingAmount = roundBookingMoney(
    summary.remaining_amount != null
      ? summary.remaining_amount
      : Math.max(0, totalAmount - totalPaid),
  );
  if (remainingAmount <= BOOKING_PAYMENT_TOLERANCE) remainingAmount = 0;
  const paymentStatus = resolveSegmentPaymentStatus(totalAmount, totalPaid, remainingAmount);
  const hasOutstandingBalance = remainingAmount > BOOKING_PAYMENT_TOLERANCE;
  return {
    hasRestaurant: totalAmount > BOOKING_PAYMENT_TOLERANCE || totalPaid > BOOKING_PAYMENT_TOLERANCE,
    totalAmount,
    totalPaid,
    remainingAmount,
    paymentStatus,
    isDue: hasOutstandingBalance,
    showPaidBreakdown: totalPaid > 0 && hasOutstandingBalance,
    showDueBreakdown: hasOutstandingBalance,
    currency,
  };
}

export function resolveBookingPaymentDisplay(booking) {
  const hotel = resolveHotelPaymentSlice(booking);
  const restaurant = resolveRestaurantPaymentSlice(booking, hotel.currency);
  const hasRestaurant = Boolean(restaurant.hasRestaurant);

  if (!hasRestaurant) {
    return {
      ...hotel,
      hasRestaurant: false,
      hotel,
      restaurant: null,
      combined: hotel,
    };
  }

  const combinedTotal = roundBookingMoney(hotel.totalAmount + restaurant.totalAmount);
  const combinedPaid = roundBookingMoney(hotel.totalPaid + restaurant.totalPaid);
  let combinedRemaining = roundBookingMoney(
    Math.max(0, hotel.remainingAmount + restaurant.remainingAmount),
  );
  if (combinedRemaining <= BOOKING_PAYMENT_TOLERANCE) combinedRemaining = 0;
  const combinedStatus = resolveSegmentPaymentStatus(combinedTotal, combinedPaid, combinedRemaining);
  const combinedDue = combinedRemaining > BOOKING_PAYMENT_TOLERANCE;
  const combined = {
    currency: hotel.currency,
    totalAmount: combinedTotal,
    totalPaid: combinedPaid,
    remainingAmount: combinedRemaining,
    paymentStatus: combinedStatus,
    isDue: combinedDue,
    showPaidBreakdown: combinedPaid > 0 && combinedDue,
    showDueBreakdown: combinedDue,
  };

  return {
    // Primary (card total) reflects combined when restaurant exists
    ...combined,
    hasRestaurant: true,
    hotel,
    restaurant,
    combined,
  };
}

export function formatBookingCurrency(amount, currency = 'INR') {
  const value = amount != null && amount !== '' ? Number(amount) : 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function isDelayedCheckout(checkoutDate, hotelCheckoutTime) {
  if (!checkoutDate) return false;
  const checkout = new Date(checkoutDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  checkout.setHours(0, 0, 0, 0);
  if (checkout > today) return false;
  if (checkout < today) return true;
  if (!hotelCheckoutTime) return false;
  const [hours, minutes] = String(hotelCheckoutTime).split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(hours || 11, minutes || 0, 0, 0);
  return new Date() >= cutoff;
}

/** Pre-arrival statuses share the same card accent as confirmed on check-in. */
export function normalizeBookingStatusValue(booking) {
  const raw = String(booking?.status_value ?? booking?.status ?? '').trim();
  if (!raw || raw === '—') return '';
  return raw.toLowerCase().replace(/\s+/g, '_');
}

/** Confirmed / pre-arrival booking accent (orange). */
export const BOOKING_CONFIRMED_COLOR = '#ff9800';
/** Checked-in / in-house accent (green). */
export const BOOKING_CHECKED_IN_COLOR = '#4caf50';

export function getBookingStatusColorKey(status) {
  const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'no_show_pending') {
    return 'no_show_pending';
  }
  if (['confirmed', 'reserved', 'pending', 'booked'].includes(normalized)) {
    return 'confirmed';
  }
  return normalized;
}

export function getBookingStatusColor(status, isPendingCheckout = false) {
  switch (getBookingStatusColorKey(status)) {
    case 'confirmed':
      return BOOKING_CONFIRMED_COLOR;
    case 'checked_in':
      return isPendingCheckout ? BOOKING_CONFIRMED_COLOR : BOOKING_CHECKED_IN_COLOR;
    case 'partial_checkin':
      return BOOKING_CHECKED_IN_COLOR;
    case 'partial_checkout':
      return isPendingCheckout ? BOOKING_CONFIRMED_COLOR : BOOKING_CHECKED_IN_COLOR;
    case 'checked_out':
      return '#9e9e9e';
    case 'no_show':
      return '#ef5350';
    case 'no_show_pending':
      return '#ff9800';
    case 'cancelled':
    case 'canceled':
      return '#ef5350';
    default:
      return '#757575';
  }
}

export function getBookingStatusLabel(status, booking = null) {
  const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
  const isPendingCheckout = booking
    ? (booking.is_pending_checkout !== undefined && booking.is_pending_checkout !== null
        ? !!booking.is_pending_checkout
        : (normalized === 'checked_in' ||
            normalized === 'partial_checkin' ||
            normalized === 'partial_checkout') &&
          isDelayedCheckout(booking.check_out_date, booking.hotel_checkout_time))
    : false;

  switch (normalized) {
    case 'confirmed':
      return 'Confirmed';
    case 'checked_in':
      return isPendingCheckout ? 'Pending Checkout' : 'Checked In';
    case 'partial_checkin':
      return isPendingCheckout ? 'Pending Checkout' : 'Partial Check-In';
    case 'partial_checkout':
      return isPendingCheckout ? 'Pending Checkout' : 'Partially Checked Out';
    case 'checked_out':
      return 'Checked Out';
    case 'booked':
      return 'Booked';
    case 'reserved':
      return 'Reserved';
    case 'pending':
      return 'Pending';
    case 'no_show':
      return 'No Show';
    case 'no_show_pending':
      return 'No Show Pending';
    case 'cancelled':
    case 'canceled':
      return 'Cancelled';
    default:
      return status || 'Unknown';
  }
}

export function getPartialStayNotice(status) {
  const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'partial_checkin') {
    return 'Partial check-in — assign remaining rooms';
  }
  if (normalized === 'partial_checkout') {
    return 'Partially checked out — review room status';
  }
  return null;
}

export function resolveBookingStatus(booking) {
  if (booking?.no_show_status === 'pending') {
    const statusValue = 'no_show_pending';
    return {
      statusValue,
      isPendingCheckout: false,
      statusColor: getBookingStatusColor(statusValue, false),
      statusLabel: getBookingStatusLabel(statusValue, booking),
    };
  }
  const statusValue = normalizeBookingStatusValue(booking);
  const isPendingCheckout =
    booking?.is_pending_checkout !== undefined && booking?.is_pending_checkout !== null
      ? !!booking.is_pending_checkout
      : (statusValue === 'checked_in' ||
          statusValue === 'partial_checkin' ||
          statusValue === 'partial_checkout') &&
        isDelayedCheckout(booking?.check_out_date, booking?.hotel_checkout_time);
  return {
    statusValue,
    isPendingCheckout,
    statusColor: getBookingStatusColor(statusValue, isPendingCheckout),
    statusLabel: getBookingStatusLabel(statusValue, booking),
  };
}

export function getPaymentStatusLabel(paymentStatus) {
  switch (paymentStatus) {
    case 'full_payment':
      return 'Full Payment';
    case 'partial_payment':
      return 'Partial Payment';
    case 'due':
      return 'Due';
    case 'no_payment':
      return 'No Payment';
    default:
      return 'Unknown';
  }
}

export function getPaymentStatusColor(paymentStatus) {
  switch (paymentStatus) {
    case 'full_payment':
      return '#4ce1b6';
    case 'partial_payment':
      return '#ff9800';
    case 'due':
      return '#ff7043';
    case 'no_payment':
      return '#9e9e9e';
    default:
      return '#757575';
  }
}

export function getPaymentStatusIcon(paymentStatus, fontSize = 16) {
  const sx = { fontSize };
  switch (paymentStatus) {
    case 'full_payment':
      return <CheckCircle sx={sx} />;
    case 'partial_payment':
      return <Pending sx={sx} />;
    case 'due':
      return <Warning sx={sx} />;
    default:
      return <Warning sx={sx} />;
  }
}

export function getGuestInitials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
}

export function getUniqueRoomTypes(booking) {
  if (!booking?.booking_items?.length) return [];
  return [...new Set(
    booking.booking_items
      .map((item) => item.room_type_name || item.room_type?.name)
      .filter(Boolean),
  )];
}
